import { mkdir, readFile, writeFile } from "node:fs/promises";
import { join } from "node:path";
import { getAspConfigDir } from "../config.js";

interface PublishRegistryEntry {
  submission_id: string;
  platform_url: string;
  plugin_id?: string;
  skill_id?: string;
  source_url?: string;
  updated_at: string;
}

interface PublishRegistry {
  entries: PublishRegistryEntry[];
}

const REGISTRY_FILE = "publish-submissions.json";
const MAX_ENTRIES = 200;

function normalizePlatformUrl(url: string): string {
  return url.replace(/\/$/, "");
}

async function loadRegistry(): Promise<PublishRegistry> {
  try {
    const path = join(getAspConfigDir(), REGISTRY_FILE);
    const raw = await readFile(path, "utf8");
    return JSON.parse(raw) as PublishRegistry;
  } catch {
    return { entries: [] };
  }
}

async function saveRegistry(registry: PublishRegistry): Promise<void> {
  const dir = getAspConfigDir();
  await mkdir(dir, { recursive: true });
  const path = join(dir, REGISTRY_FILE);
  await writeFile(path, `${JSON.stringify(registry, null, 2)}\n`, "utf8");
}

export async function recordPluginPublish(opts: {
  submissionId: string;
  platformUrl: string;
  pluginId?: string;
  sourceUrl?: string;
}): Promise<void> {
  const registry = await loadRegistry();
  const platform = normalizePlatformUrl(opts.platformUrl);
  const now = new Date().toISOString();

  registry.entries = registry.entries.filter((entry) => {
    if (entry.platform_url !== platform) return true;
    if (entry.submission_id === opts.submissionId) return false;
    if (opts.pluginId && entry.plugin_id === opts.pluginId) return false;
    return true;
  });

  registry.entries.unshift({
    submission_id: opts.submissionId,
    platform_url: platform,
    plugin_id: opts.pluginId,
    source_url: opts.sourceUrl,
    updated_at: now,
  });

  registry.entries = registry.entries.slice(0, MAX_ENTRIES);
  await saveRegistry(registry);
}

export async function lookupPluginSubmissionId(
  ref: string,
  platformUrl: string,
): Promise<string | undefined> {
  return lookupArtifactSubmissionId(ref, platformUrl, "plugin");
}

export async function recordSkillPublish(opts: {
  submissionId: string;
  platformUrl: string;
  skillId?: string;
  sourceUrl?: string;
}): Promise<void> {
  const registry = await loadRegistry();
  const platform = normalizePlatformUrl(opts.platformUrl);
  const now = new Date().toISOString();

  registry.entries = registry.entries.filter((entry) => {
    if (entry.platform_url !== platform) return true;
    if (entry.submission_id === opts.submissionId) return false;
    if (opts.skillId && entry.skill_id === opts.skillId) return false;
    return true;
  });

  registry.entries.unshift({
    submission_id: opts.submissionId,
    platform_url: platform,
    skill_id: opts.skillId,
    source_url: opts.sourceUrl,
    updated_at: now,
  });

  registry.entries = registry.entries.slice(0, MAX_ENTRIES);
  await saveRegistry(registry);
}

export async function lookupSkillSubmissionId(
  ref: string,
  platformUrl: string,
): Promise<string | undefined> {
  return lookupArtifactSubmissionId(ref, platformUrl, "skill");
}

async function lookupArtifactSubmissionId(
  ref: string,
  platformUrl: string,
  artifactType: "plugin" | "skill",
): Promise<string | undefined> {
  const registry = await loadRegistry();
  const platform = normalizePlatformUrl(platformUrl);
  const refLower = ref.toLowerCase();

  for (const entry of registry.entries) {
    if (entry.platform_url !== platform) continue;
    if (entry.submission_id === ref) return entry.submission_id;
    const artifactId = artifactType === "plugin" ? entry.plugin_id : entry.skill_id;
    if (artifactId === ref) return entry.submission_id;
    if (entry.source_url?.toLowerCase().includes(refLower)) return entry.submission_id;
  }

  return undefined;
}
